import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, TrendingUp, Users, Heart, MessageSquare, 
  Star, Zap, BarChart3, Target, Sparkles, Camera,
  ThumbsUp, Clock, Award, Crown, RefreshCw
} from 'lucide-react';
import PersonalityProfile from './PersonalityProfile';
import axios from 'axios';

const PremiumDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState({
    analytics: {
      profileViews: 0,
      matchRate: 0,
      responseRate: 0,
      sessionsCompleted: 0
    },
    profileScore: {
      overall: 0,
      photo: 0,
      bio: 0,
      preferences: 0
    },
    suggestions: [],
    recentMatches: []
  });

  useEffect(() => {
    fetchPremiumDashboard();
  }, []);

  const fetchPremiumDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/premium/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDashboardData(response.data);
      console.log('✅ Premium dashboard loaded:', response.data);
    } catch (error) {
      console.error('❌ Error loading premium dashboard:', error);
      // Use mock data if API fails
      setDashboardData({
        analytics: {
          profileViews: 127,
          matchRate: 68,
          responseRate: 82,
          sessionsCompleted: 15,
          viewsTrend: '+12%',
          matchTrend: '+5%',
          responseTrend: '+8%'
        },
        profileScore: {
          overall: 85,
          photo: 90,
          bio: 75,
          preferences: 90
        },
        suggestions: [
          {
            id: 1,
            type: 'photo',
            title: 'Add more variety to your photos',
            description: 'Include photos showing different activities and settings',
            impact: 'high',
            icon: 'Camera'
          },
          {
            id: 2,
            type: 'bio',
            title: 'Enhance your bio',
            description: 'Add specific hobbies or interests to spark conversations',
            impact: 'medium',
            icon: 'Sparkles'
          },
          {
            id: 3,
            type: 'activity',
            title: 'Optimal activity time',
            description: 'Most matches are active 7-9 PM. Try scheduling sessions then.',
            impact: 'medium',
            icon: 'Clock'
          }
        ],
        compatibilityInsights: {
          topTraits: ['Adventurous', 'Career-focused', 'Social'],
          matchPreferences: ['Similar age range', 'Shared interests', 'Lifestyle compatibility']
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const generateIcebreaker = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/premium/icebreaker`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`AI Icebreaker: ${response.data.icebreaker}`);
    } catch (error) {
      console.error('Error generating icebreaker:', error);
      alert('Icebreaker: "I noticed we both love hiking! What\'s your favorite trail?"');
    }
  };

  const optimizeProfile = async () => {
    navigate('/premium/optimize');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-rose-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading Premium Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-900 to-rose-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/profile')}
            variant="ghost"
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Premium Dashboard</h1>
              <p className="text-white/80 text-lg">Your advanced dating insights and tools</p>
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-2 rounded-full">
              <Crown className="h-6 w-6 text-white inline mr-2" />
              <span className="text-white font-semibold">Premium Member</span>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            { id: 'compatibility', label: 'Compatibility', icon: Heart }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'bg-white text-purple-900'
                    : 'bg-white/10 text-white hover:bg-white/20'
                } px-6 py-3`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Analytics Summary */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Profile Views
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-white mb-2">
                  {dashboardData.analytics.profileViews}
                </div>
                <p className="text-green-400 text-sm">
                  {dashboardData.analytics.viewsTrend || '+12%'} this week
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Heart className="h-5 w-5 mr-2" />
                  Match Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-white mb-2">
                  {dashboardData.analytics.matchRate}%
                </div>
                <Progress value={dashboardData.analytics.matchRate} className="h-2" />
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Response Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-white mb-2">
                  {dashboardData.analytics.responseRate}%
                </div>
                <Progress value={dashboardData.analytics.responseRate} className="h-2" />
              </CardContent>
            </Card>

            {/* Profile Score */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20 md:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center">
                    <Star className="h-5 w-5 mr-2" />
                    Profile Optimization Score
                  </span>
                  <Badge className="bg-yellow-500 text-white">
                    {dashboardData.profileScore.overall}/100
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-white/80 mb-1">
                      <span>Photos Quality</span>
                      <span>{dashboardData.profileScore.photo}/100</span>
                    </div>
                    <Progress value={dashboardData.profileScore.photo} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-white/80 mb-1">
                      <span>Bio Completeness</span>
                      <span>{dashboardData.profileScore.bio}/100</span>
                    </div>
                    <Progress value={dashboardData.profileScore.bio} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-white/80 mb-1">
                      <span>Preferences Match</span>
                      <span>{dashboardData.profileScore.preferences}/100</span>
                    </div>
                    <Progress value={dashboardData.profileScore.preferences} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Zap className="h-5 w-5 mr-2" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  onClick={generateIcebreaker}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate AI Icebreaker
                </Button>
                <Button
                  onClick={optimizeProfile}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  <Target className="h-4 w-4 mr-2" />
                  Optimize Profile
                </Button>
                <Button
                  onClick={() => navigate('/premium/analytics')}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Advanced Analytics
                </Button>
                <Button
                  onClick={() => setActiveTab('compatibility')}
                  className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
                >
                  <Heart className="h-4 w-4 mr-2" />
                  View Compatibility
                </Button>
              </CardContent>
            </Card>

            {/* Personality Profile Section */}
            <div className="md:col-span-2">
              <PersonalityProfile />
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Detailed Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-white font-semibold text-lg">Engagement Metrics</h3>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white/80">Total Profile Views</span>
                        <span className="text-white font-bold text-xl">{dashboardData.analytics.profileViews}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white/80">Sessions Completed</span>
                        <span className="text-white font-bold text-xl">{dashboardData.analytics.sessionsCompleted}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/80">Average Session Length</span>
                        <span className="text-white font-bold text-xl">12 min</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-white font-semibold text-lg">Success Rates</h3>
                    <div className="bg-white/5 rounded-lg p-4">
                      <div className="mb-4">
                        <div className="flex justify-between mb-1">
                          <span className="text-white/80">Match Success</span>
                          <span className="text-white">{dashboardData.analytics.matchRate}%</span>
                        </div>
                        <Progress value={dashboardData.analytics.matchRate} className="h-2" />
                      </div>
                      <div className="mb-4">
                        <div className="flex justify-between mb-1">
                          <span className="text-white/80">Message Response</span>
                          <span className="text-white">{dashboardData.analytics.responseRate}%</span>
                        </div>
                        <Progress value={dashboardData.analytics.responseRate} className="h-2" />
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-white/80">Second Date Rate</span>
                          <span className="text-white">45%</span>
                        </div>
                        <Progress value={45} className="h-2" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-start">
                    <TrendingUp className="h-5 w-5 text-green-400 mr-3 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">Peak Activity Times</h4>
                      <p className="text-white/80 text-sm">
                        Your matches are most active between <strong>7-9 PM</strong> on weekdays 
                        and <strong>2-5 PM</strong> on weekends. Schedule sessions during these times for better results!
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Compatibility Tab */}
        {activeTab === 'compatibility' && (
          <div className="space-y-6">
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white text-2xl">Compatibility Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-4">Your Top Compatibility Traits</h3>
                    <div className="flex flex-wrap gap-3">
                      {dashboardData.compatibilityInsights?.topTraits?.map((trait, index) => (
                        <Badge
                          key={index}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 text-base"
                        >
                          {trait}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-white font-semibold text-lg mb-4">What Your Matches Look For</h3>
                    <div className="space-y-3">
                      {dashboardData.compatibilityInsights?.matchPreferences?.map((pref, index) => (
                        <div key={index} className="flex items-center bg-white/5 rounded-lg p-3">
                          <ThumbsUp className="h-5 w-5 text-green-400 mr-3" />
                          <span className="text-white">{pref}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start">
                      <Award className="h-5 w-5 text-blue-400 mr-3 mt-1" />
                      <div>
                        <h4 className="text-white font-semibold mb-1">Smart Compatibility Analysis</h4>
                        <p className="text-white/80 text-sm">
                          Our system combines intelligent algorithms with AI insights to evaluate your relationship potential across 20+ key compatibility factors including personality, interests, values, lifestyle, and communication style.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumDashboard;
